import Footer from '@/components/Footer';
import Image from 'next/image';
import Link from 'next/link';
import HomepageHeader from '@/components/HomepageHeader';

export default function Services() {
  return (
    <div className="min-h-screen bg-[#F6EFED] page-transition">
      {/* Header Component */}
      <HomepageHeader />

      {/* Hero Section - Same as Homepage */}
      <section className="relative min-h-[720px] flex items-center">
        {/* Background Image/Video */}
        <div className="absolute inset-0">
          <Image
            src="/hero-banner.jpg"
            alt="Women working out in gym"
            fill
            className="object-cover"
            priority
          />
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-[#45394B]/60" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
              Our Services
            </h1>
            <p className="text-lg text-white/90 mb-8 max-w-xl">
              Comprehensive fitness solutions designed specifically for women at every stage of their wellness journey.
            </p>
          </div>
        </div>
      </section>

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 -mt-10">
        {/* Services Grid */}
        <div className="bg-white rounded-[24px] p-8 md:p-12 mb-8">
          <h2 className="text-3xl font-bold text-[#2b262e] mb-10 text-center">What We Offer</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Personal Training */}
            <div className="bg-[#f5f2f0] rounded-[18px] p-6 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#8a7490]/10 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-[#8a7490]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-[#2b262e] mb-2">Personal Training</h3>
              <p className="text-[#6e6672] text-sm mb-4">One-on-one sessions with certified female trainers tailored to your specific goals.</p>
              <ul className="text-sm text-[#6e6672] space-y-1">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#8a7490] rounded-full"></span>Custom workout plans
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#8a7490] rounded-full"></span>Nutritional guidance
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#8a7490] rounded-full"></span>Progress tracking
                </li>
              </ul>
            </div>

            {/* Group Classes */}
            <div className="bg-[#f5f2f0] rounded-[18px] p-6 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#8a7490]/10 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-[#8a7490]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-[#2b262e] mb-2">Group Classes</h3>
              <p className="text-[#6e6672] text-sm mb-4">Energetic group fitness classes designed for women, from yoga to HIIT.</p>
              <ul className="text-sm text-[#6e6672] space-y-1">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#8a7490] rounded-full"></span>Yoga & Pilates
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#8a7490] rounded-full"></span>HIIT workouts
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#8a7490] rounded-full"></span>Dance fitness
                </li>
              </ul>
            </div>

            {/* Nutrition Coaching */}
            <div className="bg-[#f5f2f0] rounded-[18px] p-6 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#8a7490]/10 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-[#8a7490]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-[#2b262e] mb-2">Nutrition Coaching</h3>
              <p className="text-[#6e6672] text-sm mb-4">Expert nutritional guidance to support your fitness goals and wellness.</p>
              <ul className="text-sm text-[#6e6672] space-y-1">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#8a7490] rounded-full"></span>Meal planning
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#8a7490] rounded-full"></span>Supplement advice
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#8a7490] rounded-full"></span>Lifestyle coaching
                </li>
              </ul>
            </div>

            {/* Online Programs */}
            <div className="bg-[#f5f2f0] rounded-[18px] p-6 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#8a7490]/10 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-[#8a7490]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-[#2b262e] mb-2">Online Programs</h3>
              <p className="text-[#6e6672] text-sm mb-4">Flexible online fitness programs you can follow from anywhere, anytime.</p>
              <ul className="text-sm text-[#6e6672] space-y-1">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#8a7490] rounded-full"></span>Video tutorials
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#8a7490] rounded-full"></span>Live streaming
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#8a7490] rounded-full"></span>Mobile app access
                </li>
              </ul>
            </div>

            {/* Wellness Workshops */}
            <div className="bg-[#f5f2f0] rounded-[18px] p-6 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#8a7490]/10 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-[#8a7490]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-[#2b262e] mb-2">Wellness Workshops</h3>
              <p className="text-[#6e6672] text-sm mb-4">Educational workshops covering mental health to hormone balance.</p>
              <ul className="text-sm text-[#6e6672] space-y-1">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#8a7490] rounded-full"></span>Stress management
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#8a7490] rounded-full"></span>Hormone health
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#8a7490] rounded-full"></span>Mindfulness
                </li>
              </ul>
            </div>

            {/* Fitness Challenges */}
            <div className="bg-[#f5f2f0] rounded-[18px] p-6 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#8a7490]/10 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-[#8a7490]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-[#2b262e] mb-2">Fitness Challenges</h3>
              <p className="text-[#6e6672] text-sm mb-4">Motivating fitness challenges to keep you engaged and pushing forward.</p>
              <ul className="text-sm text-[#6e6672] space-y-1">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#8a7490] rounded-full"></span>30-day challenges
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#8a7490] rounded-full"></span>Team competitions
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#8a7490] rounded-full"></span>Reward systems
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-[#8a7490] rounded-[24px] p-8 md:p-12 text-center mb-8">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">Choose the service that best fits your goals and start your transformation today. Our team is here to support you every step of the way.</p>
          <button className="px-8 py-3 bg-white text-[#8a7490] font-medium rounded-xl hover:bg-white/90 transition-colors">
            Explore Programs
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
}
